import { useMembership } from '@/features/account/components/Membership/useMembership'
import { useIsUserExempt } from '@/features/account/services/User/hooks'
import {
  useQueryEBTEnrollments,
  useQueryStudentEnrollments,
  useQuerySubscriptionHistory,
} from '@/features/account/services/MembershipPlan/queries'
import { epochSecondsToISO } from '@/features/account/services/MembershipPlan/utils'
import { formatDate, getDaysUntilExpire } from '@/features/shared/utils/date'

export const useIsMembershipCancelled = () => {
  const { id, cancelAtPeriodEnd, renewalDate, hasMembershipFetched } = useMembership()
  const isExempt = useIsUserExempt()

  const isEligible = Boolean(hasMembershipFetched && !isExempt && id)
  const isMembershipCancelled = Boolean(cancelAtPeriodEnd && renewalDate)

  return isEligible && isMembershipCancelled
}

export const useGetStudentEnrollmentStatus = (isEnabled = true) => {
  const { data, isPending } = useQueryStudentEnrollments(isEnabled)

  const { email_verified, reverify_before, email, expires_at } = data ?? {}

  const isStudentEnrollmentExpired = expires_at ? new Date(expires_at) < new Date() : false

  const isExpiring =
    expires_at && reverify_before ? new Date(expires_at) <= new Date(reverify_before) : false

  // we want to surface reverification messaging if the users verification expires within 30 days
  const needsReverification =
    !isExpiring && reverify_before && new Date(reverify_before) > new Date()
      ? getDaysUntilExpire(reverify_before) <= 30
      : false

  const hasReverificationWindowPassed = reverify_before && new Date(reverify_before) < new Date()

  return {
    isStudentVerified: email_verified || false,
    reverifyBefore: formatDate(new Date(reverify_before ?? ''), 'MMM d, yyyy'),
    hasReverificationWindowPassed,
    needsReverification,
    enrolledEmail: email ?? '',
    isStudentEnrollmentExpired,
    isExpiring,
    expirationDate: formatDate(new Date(expires_at ?? ''), 'MMM d, yyyy'),
    isPending,
  }
}

export const useGetSnapEBTEnrollmentStatus = (isEnabled = true) => {
  const { data } = useQueryEBTEnrollments(isEnabled)
  const { reverify_before } = data ?? {}

  const needsReverification =
    reverify_before && new Date(reverify_before) > new Date()
      ? getDaysUntilExpire(reverify_before) <= 30
      : false

  const hasReverificationWindowPassed = reverify_before && new Date(reverify_before) < new Date()

  return {
    reverifyBefore: formatDate(reverify_before ?? '', 'MMM d, yyyy'),
    needsReverification,
    hasReverificationWindowPassed,
  }
}

export const useGetFirstSubscriptionStartYear = () => {
  const { data } = useQuerySubscriptionHistory()
  // backend returns the plans in reverse chronological order (most recent first)
  const firstSubscription = data?.at(-1)
  const startDate = new Date(epochSecondsToISO(firstSubscription?.start))
  return startDate.getFullYear()
}
